import store_laboratoire from '@/store/mods/analyses/laboratoire'
import { flattenAnalysisResult, classifyResultValue, referenceLabel } from '@/utils/analysis-result-reference.mjs'

const message = (error, fallback) => error && error.response && error.response.data && typeof error.response.data.message === 'string' ? error.response.data.message : fallback

// Une instance par analyse : mêmes opérations et contrôles pour la relecture groupée et la rectification.
export default {
    data() {
        return {
            modal: false, selection: null, interpretation: '', retour: false, motif_retour: '',
            retour_technique: '', categorie_non_conformite: '', motif_retour_technique: '',
            is_saving: false, erreur_modal: '', info: '', detruit: false,
            mode: 'validation', brouillon: [], motif_rectification: '',
            alerte: { personne_prevenue: '', prevenu_at: '' }, valeurs_critiques_serveur: []
        }
    },
    computed: {
        // Toujours un booléen : sous Vue 2, `:disabled` sur une chaîne vide ('') désactive le bouton.
        retour_en_cours() {
            return this.retour === true || this.retour_technique !== ''
        },

        validation_bloquee() {
            return this.is_saving === true || this.retour_en_cours
        },

        // Lecture du résultat : lignes plates (libellé, valeur, unité, référence, alerte) quelle que soit la mise en page
        lecture() {
            const resultat = this.selection && this.selection.resultat ? this.selection.resultat : null
            if (!resultat || !Array.isArray(resultat.data)) return []
            return flattenAnalysisResult(resultat.data).map(item => ({
                section: item.section === 'Résultats' ? '' : item.section,
                libelle: item.field.label || '',
                valeur: this.texte(item.field.value),
                unite: item.field.unit || '',
                reference: referenceLabel(item.field),
                alerte: item.field.type === 'number' ? classifyResultValue(item.field.value, item.field.reference || {}) : null
            }))
        },

        documents() {
            return this.selection && Array.isArray(this.selection.documents) ? this.selection.documents : []
        },

        // LAB-007 : valeurs hors seuils critiques (annoncées par la liste, ou renvoyées par la Clinique au refus)
        valeurs_critiques() {
            if (this.valeurs_critiques_serveur.length) return this.valeurs_critiques_serveur
            return (this.mode === 'validation' && this.selection && Array.isArray(this.selection.valeurs_critiques)) ? this.selection.valeurs_critiques : []
        },

        // LAB-007 : rectification — chaque valeur modifiable du brouillon, avec la valeur validée en regard
        champs_rectification() {
            if (this.mode !== 'rectification' || !Array.isArray(this.brouillon) || !this.brouillon.length) return []
            const original = (this.selection && this.selection.resultat && Array.isArray(this.selection.resultat.data)) ? this.selection.resultat.data : []
            const entrees = []
            if (this.brouillon.every(section => section && Array.isArray(section.fields))) {
                this.brouillon.forEach((section, s) => section.fields.forEach((field, f) => {
                    const avant = (original[s] && Array.isArray(original[s].fields) && original[s].fields[f]) ? original[s].fields[f].value : null
                    entrees.push({ cle: s + '-' + f, section: section.title || '', libelle: field.label || '', type: field.type || 'text', options: Array.isArray(field.options) ? field.options : [], unite: field.unit || '', reference: referenceLabel(field), avant: this.texte(avant), cible: field })
                }))
                return entrees
            }
            const entetes = (this.brouillon[0] && Array.isArray(this.brouillon[0].cells)) ? this.brouillon[0].cells : []
            const nettoyer = valeur => String(valeur === null || valeur === undefined ? '' : valeur).replace(/<[^>]*>/g, '')
            this.brouillon.forEach((row, r) => {
                if (r === 0 || !row || !Array.isArray(row.cells)) return
                const nom = row.cells[0] && row.cells[0].value ? nettoyer(row.cells[0].value) : 'Ligne ' + r
                row.cells.forEach((cell, c) => {
                    if (c === 0 || !cell || cell.merged) return
                    const entete = entetes.find((item, debut) => item && !item.merged && debut <= c && debut + (Number(item.colspan) || 1) > c)
                    const colonne = entete && entete.value ? nettoyer(entete.value) : 'Colonne ' + (c + 1)
                    const avant_cellule = (original[r] && Array.isArray(original[r].cells)) ? original[r].cells[c] : null
                    if (Array.isArray(cell.subcells) && cell.subcells.length) {
                        cell.subcells.forEach((sous, k) => entrees.push({ cle: r + '-' + c + '-' + k, section: '', libelle: nom + ' — ' + colonne + ' / ' + (k + 1), type: 'text', options: [], unite: '', reference: '', avant: this.texte(nettoyer(avant_cellule && Array.isArray(avant_cellule.subcells) && avant_cellule.subcells[k] ? avant_cellule.subcells[k].value : null)), cible: sous }))
                    } else {
                        entrees.push({ cle: r + '-' + c, section: '', libelle: nom + ' — ' + colonne, type: 'text', options: [], unite: '', reference: '', avant: this.texte(nettoyer(avant_cellule ? avant_cellule.value : null)), cible: cell })
                    }
                })
            })
            return entrees
        }
    },
    beforeDestroy() { this.detruit = true },
    methods: {
        apres_action() {
            this.modal = false
            return this.charger()
        },
        texte(valeur) {
            if (valeur === true) return 'Oui'
            if (valeur === false) return 'Non'
            return (valeur === null || valeur === undefined || valeur === '') ? '—' : String(valeur)
        },

        ouvrir(ligne) {
            this.mode = 'validation'
            this.selection = ligne
            this.interpretation = ligne.interpretation_biologiste || ''
            this.retour = false
            this.motif_retour = ''
            this.retour_technique = ''
            this.categorie_non_conformite = ''
            this.motif_retour_technique = ''
            this.erreur_modal = ''
            this.reinitialiser_alerte()
            this.brouillon = []
            this.motif_rectification = ''
            this.modal = true
        },

        // LAB-007 : rectification d'un résultat validé — l'original reste visible, une nouvelle version est créée avec le motif
        ouvrir_rectification(ligne) {
            this.mode = 'rectification'
            this.selection = ligne
            this.interpretation = ligne.interpretation_biologiste || ''
            this.retour = false
            this.motif_retour = ''
            this.retour_technique = ''
            this.categorie_non_conformite = ''
            this.motif_retour_technique = ''
            this.motif_rectification = ''
            this.erreur_modal = ''
            this.reinitialiser_alerte()
            this.brouillon = (ligne.resultat && Array.isArray(ligne.resultat.data)) ? JSON.parse(JSON.stringify(ligne.resultat.data)) : []
            this.modal = true
        },

        reinitialiser_alerte() {
            this.alerte = { personne_prevenue: '', prevenu_at: '' }
            this.valeurs_critiques_serveur = []
        },

        alerte_payload() {
            const personne = this.alerte.personne_prevenue ? this.alerte.personne_prevenue.trim() : ''
            const quand = this.alerte.prevenu_at ? String(this.alerte.prevenu_at).replace('T', ' ').substr(0, 16) : ''
            return (personne || quand) ? { personne_prevenue: personne, prevenu_at: quand } : null
        },

        signaler_critiques(error) {
            const data = (error && error.response && error.response.data) ? error.response.data : null
            if (data && Array.isArray(data.valeurs_critiques) && data.valeurs_critiques.length) this.valeurs_critiques_serveur = data.valeurs_critiques
        },

        rectifier() {
            if (!this.selection || this.is_saving) return Promise.resolve()
            if (!this.motif_rectification.trim()) {
                this.erreur_modal = 'Indiquez le motif de la rectification.'
                return Promise.resolve()
            }
            this.is_saving = true
            this.erreur_modal = ''
            const ligne = this.selection
            const charge = { motif: this.motif_rectification.trim(), data: JSON.parse(JSON.stringify(this.brouillon)), interpretation: this.interpretation.trim() || null, alerte: this.alerte_payload() }
            return store_laboratoire.rectifierResultat(ligne.demande_id, charge).then((response) => {
                if (this.detruit) return
                if (!response || response.success !== true) throw new Error('Rectification refusée')
                this.info = (response.message || 'Résultat rectifié.') + ' ' + ligne.analyse + ' (' + ligne.patient + ').'
                this.is_saving = false
                return this.apres_action()
            }).catch((error) => {
                if (this.detruit) return
                this.is_saving = false
                this.signaler_critiques(error)
                this.erreur_modal = message(error, 'La rectification n\'a pas été enregistrée. Actualisez puis réessayez.')
            })
        },

        bouton_retour() {
            if (this.retour) return this.renvoyer()
            this.retour = true
            this.retour_technique = ''
            this.erreur_modal = ''
            return Promise.resolve()
        },

        bouton_retour_technique(type) {
            if (this.retour_technique === type) return this.renvoyer_technique()
            this.retour_technique = type
            this.retour = false
            this.erreur_modal = ''
            return Promise.resolve()
        },

        // Le biologiste renonce au retour (saisie, contrôle technique ou non-conformité) :
        // la fenêtre revient à la relecture et « Valider et diffuser » redevient disponible.
        annuler_retour() {
            if (this.is_saving) return
            this.retour = false
            this.motif_retour = ''
            this.retour_technique = ''
            this.categorie_non_conformite = ''
            this.motif_retour_technique = ''
            this.erreur_modal = ''
        },

        renvoyer_technique() {
            if (!this.selection || this.is_saving) return Promise.resolve()
            if (!this.motif_retour_technique.trim()) {
                this.erreur_modal = 'Indiquez le motif du retour en technique.'
                return Promise.resolve()
            }
            if (this.retour_technique === 'non_conformite' && !this.categorie_non_conformite) {
                this.erreur_modal = 'Choisissez la catégorie de la non-conformité.'
                return Promise.resolve()
            }
            this.is_saving = true
            this.erreur_modal = ''
            const ligne = this.selection
            return store_laboratoire.renvoyerEnTechnique(ligne.demande_id, {
                type: this.retour_technique, categorie: this.categorie_non_conformite || null, motif: this.motif_retour_technique.trim()
            }).then(response => {
                if (this.detruit) return
                if (!response || response.success !== true) throw new Error('Retour refusé')
                this.info = 'Analyse renvoyée en technique : ' + ligne.analyse + ' (' + ligne.patient + ').'
                this.is_saving = false
                return this.apres_action()
            }).catch(error => {
                if (this.detruit) return
                this.is_saving = false
                this.erreur_modal = message(error, 'Le retour en technique n’a pas été enregistré. Réessayez après actualisation.')
            })
        },

        valider() {
            if (!this.selection || this.is_saving) return Promise.resolve()
            this.is_saving = true
            this.erreur_modal = ''
            const ligne = this.selection
            return store_laboratoire.validerResultat(ligne.demande_id, { interpretation: this.interpretation.trim() || null, alerte: this.alerte_payload() }).then((response) => {
                if (this.detruit) return
                if (!response || response.success !== true) throw new Error('Validation refusée')
                this.info = 'Résultat validé et diffusé : ' + ligne.analyse + ' (' + ligne.patient + ').'
                this.is_saving = false
                return this.apres_action()
            }).catch((error) => {
                if (this.detruit) return
                this.is_saving = false
                this.signaler_critiques(error)
                this.erreur_modal = message(error, 'La validation n\'a pas été enregistrée. Actualisez puis réessayez.')
            })
        },

        renvoyer() {
            if (!this.selection || this.is_saving) return Promise.resolve()
            if (!this.motif_retour.trim()) {
                this.erreur_modal = 'Indiquez le motif du retour à la saisie.'
                return Promise.resolve()
            }
            this.is_saving = true
            this.erreur_modal = ''
            const ligne = this.selection
            return store_laboratoire.renvoyerEnSaisie(ligne.demande_id, { motif: this.motif_retour.trim() }).then((response) => {
                if (this.detruit) return
                if (!response || response.success !== true) throw new Error('Retour refusé')
                this.info = 'Analyse renvoyée à la saisie : ' + ligne.analyse + ' (' + ligne.patient + ').'
                this.is_saving = false
                return this.apres_action()
            }).catch((error) => {
                if (this.detruit) return
                this.is_saving = false
                this.erreur_modal = message(error, 'Le retour à la saisie n\'a pas été enregistré. Actualisez puis réessayez.')
            })
        },

        ouvrir_document(doc) {
            if (!this.selection) return Promise.resolve()
            return store_laboratoire.getDocumentBlob(this.selection.demande_id, doc.id).then((blob) => {
                if (typeof window === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return
                window.open(URL.createObjectURL(blob), '_blank', 'noopener')
            }).catch((error) => {
                this.erreur_modal = message(error, 'Le document n\'a pas pu être ouvert.')
            })
        },

        date_heure(value) {
            if (!value) return 'Non renseignée'
            const texte = String(value).replace('T', ' ')
            const morceaux = texte.split(' ')
            const date = morceaux[0].split('-')
            if (date.length !== 3) return texte
            const heure = morceaux.length > 1 ? morceaux[1].substr(0, 5) : ''
            return date[2] + '/' + date[1] + '/' + date[0] + (heure ? ' ' + heure : '')
        }
    }
}
